import { useCallback, useEffect, useRef, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { Bot, Check, Loader2, Send, X } from "lucide-react";

type Msg = { role: "user" | "assistant"; content: string };
type Approval = { id: string; action: string; args_hash: string; preview: { before?: unknown; after?: unknown; reason?: string }; created_at: string };

const SUGGESTIONS = [
  "Comment se portent mes ventes sur 30 jours ?",
  "Liste mes produits",
  "Prépare un brief de lancement",
];

const ACTION_LABELS: Record<string, string> = {
  update_product: "Modification de produit",
  propose_brief: "Brief de lancement",
};

const fmt = (v: unknown) => (v === null || v === undefined ? "—" : typeof v === "string" ? v : JSON.stringify(v, null, 2));

async function invoke<T>(name: string, body: unknown): Promise<T> {
  const { data, error } = await supabase.functions.invoke(name, { body });
  if (error) {
    let msg = "Une erreur est survenue.";
    try { msg = (await (error as { context?: Response }).context?.json())?.error ?? msg; } catch { /* keep default */ }
    throw new Error(typeof msg === "string" ? msg : "Requête invalide.");
  }
  return data as T;
}

function ApprovalInbox({ refreshKey }: { refreshKey: number }) {
  const { toast } = useToast();
  const [items, setItems] = useState<Approval[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try { setItems((await invoke<{ approvals: Approval[] }>("ai-approvals", { op: "list" })).approvals ?? []); }
    catch (e) { toast({ title: "Approbations indisponibles", description: (e as Error).message, variant: "destructive" }); }
    finally { setLoading(false); }
  }, [toast]);

  useEffect(() => { load(); }, [load, refreshKey]);

  const decide = async (a: Approval, op: "approve" | "reject") => {
    setBusy(a.id);
    try {
      await invoke("ai-approvals", op === "approve" ? { op, id: a.id, args_hash: a.args_hash } : { op, id: a.id });
      toast({ title: op === "approve" ? "Action approuvée et appliquée" : "Proposition rejetée" });
      setItems((xs) => xs.filter((x) => x.id !== a.id));
    } catch (e) {
      toast({ title: "Échec", description: (e as Error).message, variant: "destructive" });
    } finally { setBusy(null); }
  };

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle className="text-lg">Boîte d'approbation</CardTitle>
        <Badge variant="secondary">{items.length} en attente</Badge>
      </CardHeader>
      <CardContent className="space-y-4">
        {loading ? <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /> :
          items.length === 0 ? <p className="text-sm text-muted-foreground">Aucune action en attente.</p> :
          items.map((a) => (
            <div key={a.id} className="rounded-lg border border-border p-4 space-y-3">
              <div className="flex items-center justify-between gap-2">
                <p className="font-semibold">{ACTION_LABELS[a.action] ?? a.action}</p>
                <span className="text-xs text-muted-foreground">{new Date(a.created_at).toLocaleString("fr-FR")}</span>
              </div>
              {a.preview.reason && <p className="text-sm text-muted-foreground">{a.preview.reason}</p>}
              <div className="grid gap-3 sm:grid-cols-2">
                <div><p className="text-xs font-medium text-muted-foreground mb-1">Avant</p>
                  <pre translate="no" className="text-xs bg-muted rounded p-2 whitespace-pre-wrap break-words max-h-48 overflow-auto">{fmt(a.preview.before)}</pre></div>
                <div><p className="text-xs font-medium text-muted-foreground mb-1">Après</p>
                  <pre translate="no" className="text-xs bg-muted rounded p-2 whitespace-pre-wrap break-words max-h-48 overflow-auto">{fmt(a.preview.after)}</pre></div>
              </div>
              <div className="flex gap-2 justify-end">
                <Button variant="outline" size="sm" disabled={busy === a.id} onClick={() => decide(a, "reject")}><X className="h-4 w-4 mr-1" />Rejeter</Button>
                <Button size="sm" disabled={busy === a.id} onClick={() => decide(a, "approve")}>
                  {busy === a.id ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Check className="h-4 w-4 mr-1" />}Approuver
                </Button>
              </div>
            </div>
          ))}
      </CardContent>
    </Card>
  );
}

export function AssistantTab() {
  const { toast } = useToast();
  const [stores, setStores] = useState<{ id: string; store_name: string | null }[]>([]);
  const [storeId, setStoreId] = useState<string>("");
  const [msgs, setMsgs] = useState<Msg[]>([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const endRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    (async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;
      const { data } = await supabase.from("store_settings").select("id, store_name").eq("user_id", user.id).order("created_at", { ascending: false });
      setStores(data ?? []);
      if (data?.[0]) setStoreId(data[0].id);
    })();
  }, []);

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth", block: "nearest" }); }, [msgs]);

  const send = async (text: string) => {
    const message = text.trim();
    if (!message || !storeId || sending) return;
    const history = msgs.slice(-10);
    setMsgs((m) => [...m, { role: "user", content: message }]);
    setInput("");
    setSending(true);
    try {
      const r = await invoke<{ reply: string; approvals: unknown[] }>("ai-orchestrator", { message, store_id: storeId, history });
      setMsgs((m) => [...m, { role: "assistant", content: r.reply || "…" }]);
      if (r.approvals?.length) setRefreshKey((k) => k + 1);
    } catch (e) {
      toast({ title: "Assistant indisponible", description: (e as Error).message, variant: "destructive" });
      setMsgs((m) => m.slice(0, -1));
      setInput(message);
    } finally { setSending(false); }
  };

  return (
    <div className="grid gap-4 lg:grid-cols-[1.4fr_1fr]">
      <Card className="flex flex-col">
        <CardHeader className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <CardTitle className="text-lg flex items-center gap-2"><Bot className="h-5 w-5 text-primary" />Assistant</CardTitle>
          {stores.length > 0 && (
            <Select value={storeId} onValueChange={(v) => { setStoreId(v); setMsgs([]); }}>
              <SelectTrigger className="sm:w-64"><SelectValue placeholder="Boutique" /></SelectTrigger>
              <SelectContent>{stores.map((s) => <SelectItem key={s.id} value={s.id}><span translate="no">{s.store_name || "Boutique"}</span></SelectItem>)}</SelectContent>
            </Select>
          )}
        </CardHeader>
        <CardContent className="flex flex-col gap-4 flex-1">
          {stores.length === 0 ? <p className="text-sm text-muted-foreground">Créez d'abord une boutique pour utiliser l'assistant.</p> : (
            <>
              <div className="min-h-[280px] max-h-[460px] overflow-y-auto space-y-3 rounded-lg bg-muted/40 p-3">
                {msgs.length === 0 && <p className="text-sm text-muted-foreground">Posez une question sur votre boutique. Les changements sensibles vous seront toujours soumis pour approbation.</p>}
                {msgs.map((m, i) => (
                  <div key={i} className={m.role === "user" ? "flex justify-end" : "flex justify-start"}>
                    <div className={`max-w-[85%] rounded-2xl px-4 py-2 text-sm whitespace-pre-wrap ${m.role === "user" ? "bg-primary text-primary-foreground" : "bg-card border border-border"}`}>{m.content}</div>
                  </div>
                ))}
                {sending && <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />}
                <div ref={endRef} />
              </div>
              <div className="flex flex-wrap gap-2">
                {SUGGESTIONS.map((s) => <Button key={s} variant="outline" size="sm" disabled={sending} onClick={() => send(s)}>{s}</Button>)}
              </div>
              <form className="flex gap-2" onSubmit={(e) => { e.preventDefault(); send(input); }}>
                <Textarea value={input} onChange={(e) => setInput(e.target.value)} maxLength={2000} rows={2} placeholder="Votre message…"
                  onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(input); } }} />
                <Button type="submit" disabled={sending || !input.trim()} aria-label="Envoyer"><Send className="h-4 w-4" /></Button>
              </form>
            </>
          )}
        </CardContent>
      </Card>
      <ApprovalInbox refreshKey={refreshKey} />
    </div>
  );
}
